import { type ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CloseIcon, IconButton, Label, Notice, Textarea } from "../../ui/index.js";
import { shrinkImage } from "./image.js";
import { useVoiceRecorder } from "./recorder.js";
import {
  errorKey,
  FILE_ACCEPT,
  formatSeconds,
  kindOfFile,
  MAX_FILE_BYTES,
  MAX_SOURCES,
  MAX_TOTAL_BYTES,
  MAX_VOICE_SECONDS,
  type NewSource,
  type ParseDraft,
  type PickedSource,
  SourcesError,
  uploadSources,
} from "./sources.js";
import type { AnswersDraft, DraftService } from "./types.js";

/**
 * Источники владельца для черновика профиля: файлы (фото, PDF, Word, Excel, несколько сразу), голосовое,
 * записанное здесь же, и текст. «Заполнить» отправляет всё одним запросом вместе с текущим черновиком (`draft`):
 * следующий источник дополняет уже собранное, а не перезаписывает. Файлы и запись не сохраняются.
 */
export function SourcesPanel({
  salonId,
  voiceInput,
  draft,
  onParsed,
}: {
  salonId: string;
  voiceInput: boolean;
  /** Текущие услуги и ответы шага: берутся в момент нажатия «Заполнить». */
  draft: ParseDraft;
  onParsed: (r: { services: DraftService[]; answers: AnswersDraft }) => void;
}) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<PickedSource[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nextId = useRef(1);

  const count = picked.length + (text.trim() ? 1 : 0);
  const full = count >= MAX_SOURCES;
  // Сколько файлов уже выбрано держим в ref: несколько файлов из одного выбора добавляются подряд, до перерисовки.
  const pickedCount = useRef(0);
  pickedCount.current = picked.length;
  // Общий размер тоже: сервер принимает до 20 МБ за раз, лучше сказать сразу, чем после загрузки.
  const pickedBytes = useRef(0);
  pickedBytes.current = picked.reduce((n, p) => n + ("blob" in p ? p.blob.size : 0), 0);
  const add = (s: NewSource) => {
    if (pickedCount.current + (text.trim() ? 1 : 0) >= MAX_SOURCES) {
      setError(errorKey("too_many_sources"));
      return;
    }
    const size = "blob" in s ? s.blob.size : 0;
    if (pickedBytes.current + size > MAX_TOTAL_BYTES) {
      setError(errorKey("total_too_big"));
      return;
    }
    pickedCount.current += 1;
    pickedBytes.current += size;
    const item = { ...s, id: nextId.current++ } as PickedSource;
    setPicked((cur) => [...cur, item]);
  };

  const recorder = useVoiceRecorder((blob, seconds) => add({ kind: "voice", blob, seconds }));

  const onFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = "";
    setError(null);
    setPreparing(true);
    try {
      for (const file of files) {
        const kind = kindOfFile(file);
        if (kind === null) setError(errorKey("unsupported_type"));
        else if (kind === "old") setError(errorKey("old_format"));
        else if (kind === "photo") {
          // Фото уменьшаем в браузере: модели хватает 1568 px, а на мобильном интернете это в разы быстрее.
          try {
            add({ kind, name: file.name, blob: await shrinkImage(file) });
          } catch {
            setError(errorKey("photo"));
          }
        } else if (file.size > MAX_FILE_BYTES) setError(errorKey("file_too_big"));
        else add({ kind, name: file.name, blob: file });
      }
    } finally {
      setPreparing(false);
    }
  };

  const parse = async () => {
    const sources: PickedSource[] = [...picked, ...(text.trim() ? [{ id: 0, kind: "text" as const, text: text.trim() }] : [])];
    if (sources.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await uploadSources(salonId, sources, draft);
      setPicked([]);
      setText("");
      onParsed(r);
    } catch (e) {
      setError(errorKey(e instanceof SourcesError ? e.reason : "failed"));
    } finally {
      setBusy(false);
    }
  };

  const recording = recorder.status === "recording";
  const locked = busy || preparing || recording;
  const label = (s: PickedSource) =>
    s.kind === "voice"
      ? t("onb.sources.voiceItem", { time: formatSeconds(s.seconds) })
      : s.kind === "text"
        ? t("onb.sources.kind.text")
        : `${t(`onb.sources.kind.${s.kind}`)} · ${s.name}`;

  return (
    <Card className="space-y-3">
      <p className="text-sm text-ink-2">{t("onb.sources.intro")}</p>
      <div className="flex flex-wrap gap-2">
        <label
          className={`flex-1 cursor-pointer rounded-field bg-slate-100 px-4 py-3 text-center font-medium ${locked || full ? "pointer-events-none opacity-50" : ""}`}
        >
          {t("onb.sources.files")}
          <input type="file" multiple accept={FILE_ACCEPT} className="sr-only" onChange={(e) => void onFiles(e)} disabled={locked || full} />
        </label>
        {voiceInput &&
          (recording ? (
            <Button variant="secondary" className="flex-1" onClick={recorder.stop}>
              {t("onb.sources.voiceStop", { time: formatSeconds(recorder.seconds), max: formatSeconds(MAX_VOICE_SECONDS) })}
            </Button>
          ) : (
            <Button variant="secondary" className="flex-1" disabled={busy || preparing || full} onClick={recorder.start}>
              {t("onb.sources.voice")}
            </Button>
          ))}
      </div>
      <p className="text-xs text-ink-3">{t("onb.sources.filesHint", { count: MAX_SOURCES })}</p>
      {voiceInput && <p className="text-xs text-ink-3">{t("onb.sources.voiceHint")}</p>}
      {recorder.status === "denied" && <Notice kind="error">{t("onb.sources.voiceDenied")}</Notice>}
      {(recorder.status === "unsupported" || recorder.status === "failed") && <Notice kind="error">{t("onb.sources.voiceUnsupported")}</Notice>}

      <div>
        <Label htmlFor="sources-text">{t("onb.sources.textLabel")}</Label>
        <Textarea
          id="sources-text"
          rows={4}
          value={text}
          maxLength={20_000}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("onb.sources.textPlaceholder")}
          disabled={busy}
        />
      </div>

      {picked.length > 0 && (
        <ul className="space-y-1" aria-label={t("onb.sources.picked")}>
          {picked.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <span className="min-w-0 truncate">{label(s)}</span>
              <IconButton
                size="sm"
                label={t("common.remove")}
                icon={<CloseIcon />}
                disabled={busy}
                onClick={() => setPicked(picked.filter((x) => x.id !== s.id))}
              />
            </li>
          ))}
        </ul>
      )}

      {count > MAX_SOURCES && <Notice kind="error">{t(errorKey("too_many_sources"))}</Notice>}
      <Button className="w-full" disabled={locked || count === 0 || count > MAX_SOURCES} onClick={() => void parse()}>
        {count > 0 ? t("onb.sources.parseCount", { count }) : t("onb.sources.parse")}
      </Button>
      {busy && <Notice>{t("onb.sources.parsing")}</Notice>}
      {error && <Notice kind="error">{t(error)}</Notice>}
    </Card>
  );
}
